/**
 * The module tree the app is running on, and every question asked of it.
 *
 * It starts as the Asian Trails tree (`DEFAULT_MODULE_TREE`: the catalog, no
 * tenant config) and is replaced once per session by the tenant's tree,
 * resolved from `GET /api/modules` next to `/auth/me` and BEFORE the shell
 * renders (`auth/AuthContext.tsx`), so the sidebar never flickers. Signing
 * out puts the default back.
 *
 * Read it in React through `useModuleTree()` / `useModuleRegistries()` (they
 * re-render on a change); plain functions (`canSeeTile`, `pageLabel`, the
 * ⌘K index) read the current tree through the getters below.
 *
 * Asian Trails is unchanged by construction: with an empty config the tree
 * IS `DEFAULT_MODULE_TREE`, the same object every exported constant
 * (`MODULE_NAV`, `MODULE_LABEL`, …) was derived from.
 */
import { useSyncExternalStore } from 'react';
import { CATALOG, DEFAULT_MODULE_TREE, resolveModuleTree, type Catalog, type ModuleTree, type TreeModule, type TreePage } from './catalog';
import { normalizeModuleConfig, type TenantModuleConfig } from './config';
import { NO_CAN, normalizeCustomModules, withCustomModules, type CustomCan, type CustomModuleInfo } from './custom';
import {
  deriveBorrowedRoutes,
  deriveHotPlateModules,
  deriveHubTiles,
  deriveModuleIcons,
  deriveModuleLabels,
  deriveModuleNav,
  deriveModuleTiles,
  deriveOffRailLabels,
  deriveSwitcher,
  deriveTileRules,
  type HubTile,
  type ModuleDef,
  type NavGroup,
  type TileVisibilityRule,
} from './registries';
import type { ModuleSlug, TileSlug } from './ids';

// ── The store ───────────────────────────────────────────────────────────

let current: ModuleTree = DEFAULT_MODULE_TREE;
const listeners = new Set<() => void>();

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getModuleTree(): ModuleTree {
  return current;
}

export function setModuleTree(tree: ModuleTree): void {
  if (tree === current) return;
  current = tree;
  for (const fn of listeners) fn();
}

/**
 * Apply a tenant config (the `config` of `GET /api/modules`, read leniently).
 * An empty one restores `DEFAULT_MODULE_TREE` itself, so a tenant that never
 * changed anything runs on the very object the defaults were derived from.
 *
 * `unavailable` is the answer's pages still waiting for a setup (page id →
 * setup, the API's ModuleAvailability: WhatsApp before a number is
 * connected). They are switched off in the tree, so the sidebar, the module
 * switcher, the Hub home, the command bar and the routes all leave them out,
 * exactly as the API gate does. Undefined keeps the set already known.
 */
export function applyModuleConfig(raw: unknown, custom?: unknown, unavailable?: unknown): ModuleTree {
  const cfg = normalizeModuleConfig(raw);
  // The published custom modules join the catalog BEFORE resolution, so the
  // tenant's switches, names and order apply to them like to any module
  // (brief §5.4; custom.ts). None: the very CATALOG object.
  // `custom` undefined keeps the modules already known (Admin ▸ Modules
  // applies a saved config without re-reading them).
  if (custom !== undefined) currentCustom = normalizeCustomModules(custom);
  if (unavailable !== undefined) currentUnavailable = normalizeUnavailable(unavailable);
  currentCatalog = withCustomModules(CATALOG, currentCustom);
  const plain = isEmptyConfig(cfg) && currentCatalog === CATALOG && Object.keys(currentUnavailable).length === 0;
  setModuleTree(plain ? DEFAULT_MODULE_TREE : withUnavailable(resolveModuleTree(currentCatalog, cfg), currentUnavailable));
  return current;
}

export function resetModuleTree(): void {
  currentCustom = [];
  currentUnavailable = {};
  currentCatalog = CATALOG;
  setModuleTree(DEFAULT_MODULE_TREE);
}

// ── Pages waiting for a setup (2026-10-29) ───────────────────────────────

let currentUnavailable: Readonly<Record<string, string>> = {};

/** `{ "coportal.hotline-whatsapp": "whatsapp" }`, read leniently (an array of ids is accepted too). */
export function normalizeUnavailable(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (Array.isArray(raw)) {
    for (const id of raw) if (typeof id === 'string' && id) out[id] = 'setup';
    return out;
  }
  if (!raw || typeof raw !== 'object') return out;
  for (const [id, setup] of Object.entries(raw as Record<string, unknown>)) {
    if (id) out[id] = typeof setup === 'string' && setup ? setup : 'setup';
  }
  return out;
}

/** `tree` with the waiting pages (and their features) switched off. The same object when there is none. */
export function withUnavailable(tree: ModuleTree, unavailable: Readonly<Record<string, string>>): ModuleTree {
  if (Object.keys(unavailable).length === 0) return tree;
  return {
    ...tree,
    modules: tree.modules.map((m) => ({
      ...m,
      groups: m.groups.map((g) => ({
        ...g,
        pages: g.pages.map((p) =>
          unavailable[p.id]
            ? { ...p, enabled: false, features: p.features.map((f) => ({ ...f, enabled: false })) }
            : p,
        ),
      })),
    })),
  };
}

/** The setup a page still waits for ('whatsapp'), or null when it is available. */
export function pageWaitsFor(pageId: string): string | null {
  return currentUnavailable[pageId] ?? null;
}

// ── Custom modules (brief §5.4) ──────────────────────────────────────────

let currentCatalog: Catalog = CATALOG;
let currentCustom: CustomModuleInfo[] = [];

/**
 * The catalog the current tree was resolved from: the built-in one plus the
 * tenant's published custom modules. Resolve or validate a tenant config
 * against THIS (Admin ▸ Modules), so a custom module's switch, name and
 * place are edited like a built-in module's.
 */
export function getEffectiveCatalog(): Catalog {
  return currentCatalog;
}

export function useEffectiveCatalog(): Catalog {
  return useSyncExternalStore(subscribe, getEffectiveCatalog, getEffectiveCatalog);
}

/** The published custom modules, as `GET /api/modules` sent them. */
export function getCustomModules(): readonly CustomModuleInfo[] {
  return currentCustom;
}

/** What the signed-in person may do in a custom module (nothing for an unknown slug). */
export function customCan(slug: string): CustomCan {
  return currentCustom.find((c) => c.slug === slug)?.can ?? NO_CAN;
}

function isEmptyConfig(c: TenantModuleConfig): boolean {
  return (
    !Object.keys(c.modules).length &&
    !Object.keys(c.pages).length &&
    !Object.keys(c.features).length &&
    !c.order.length &&
    !c.installed.length
  );
}

export function useModuleTree(): ModuleTree {
  return useSyncExternalStore(subscribe, getModuleTree, getModuleTree);
}

// ── Derived, once per tree ──────────────────────────────────────────────

export interface ModuleRegistries {
  nav: Record<ModuleSlug, NavGroup[]>;
  labels: Record<ModuleSlug, string>;
  tiles: Record<ModuleSlug, string>;
  icons: Record<ModuleSlug, string>;
  switcher: ModuleDef[];
  offRail: Record<string, string>;
  hotPlate: ModuleSlug[];
  tileRules: Record<TileSlug, TileVisibilityRule>;
  hubTiles: HubTile[];
  /** Borrowed rows' routes → the module that shows them (`deriveBorrowedRoutes`). */
  borrowed: Record<string, ModuleSlug>;
}

interface Index {
  reg: ModuleRegistries;
  byId: Map<string, TreeModule>;
  byTile: Map<string, TreeModule>;
  pages: Map<string, TreePage>;
  features: Map<string, boolean>;
  /** Switched-off routes: [route, exact] (exact for a page that IS its module's front). */
  offRoutes: [string, boolean][];
  /** Routes whose rail row lives in another module (a placed template): [route, host], longest first. */
  hostRoutes: [string, string][];
}

const cache = new WeakMap<ModuleTree, Index>();

function index(tree: ModuleTree): Index {
  let ix = cache.get(tree);
  if (ix) return ix;
  const byId = new Map(tree.modules.map((m) => [m.id, m]));
  const byTile = new Map(tree.modules.map((m) => [m.tile, m]));
  const pages = new Map<string, TreePage>();
  const features = new Map<string, boolean>();
  const offRoutes: [string, boolean][] = [];
  const hostRoutes: [string, string][] = [];
  for (const m of tree.modules) {
    if (!m.enabled) offRoutes.push([m.route, false]);
    for (const f of m.features) features.set(f.id, f.enabled);
    for (const g of m.groups) {
      for (const p of g.pages) {
        pages.set(p.id, p);
        if (p.module !== m.id) hostRoutes.push([(p.to.split('?')[0] ?? p.to).replace(/\/+$/, ''), m.id]);
        for (const f of p.features) features.set(f.id, f.enabled);
        if (m.enabled && !p.enabled) {
          const to = (p.to.split('?')[0] ?? p.to).replace(/\/+$/, '') || '/';
          offRoutes.push([to, to === m.route]);
        }
      }
    }
  }
  ix = {
    reg: {
      nav: deriveModuleNav(tree),
      labels: deriveModuleLabels(tree),
      tiles: deriveModuleTiles(tree),
      icons: deriveModuleIcons(tree),
      switcher: deriveSwitcher(tree),
      offRail: deriveOffRailLabels(tree),
      hotPlate: deriveHotPlateModules(tree),
      tileRules: deriveTileRules(tree),
      hubTiles: deriveHubTiles(tree),
      borrowed: deriveBorrowedRoutes(tree),
    },
    byId,
    byTile,
    pages,
    features,
    offRoutes,
    hostRoutes: hostRoutes.sort((a, b) => b[0].length - a[0].length),
  };
  cache.set(tree, ix);
  return ix;
}

/** Every registry, derived from the current tree. */
export function currentRegistries(): ModuleRegistries {
  return index(current).reg;
}

export function useModuleRegistries(): ModuleRegistries {
  return index(useModuleTree()).reg;
}

// ── Questions (brief §4.3, §4.4) ────────────────────────────────────────

/**
 * A module's name wherever people read it (the Hub tile, titles, PDFs'
 * headings): the tenant's label, else the catalog's ('CS/OPS Portal' for
 * Asian Trails, 'Operations' in the demo). Unknown id: the id.
 */
export function moduleLabel(id: string, tree: ModuleTree = current): string {
  return index(tree).byId.get(id)?.label ?? id;
}

/** The sidebar / breadcrumb form of {@link moduleLabel} ('Bizdev'). */
export function moduleNavLabel(id: string, tree: ModuleTree = current): string {
  return index(tree).byId.get(id)?.navLabel ?? id;
}

/** A page's name, the tenant's when set ('Cities & areas'). Unknown id: undefined. */
export function pageLabel(pageId: string, tree: ModuleTree = current): string | undefined {
  return index(tree).pages.get(pageId)?.label;
}

/** A sidebar section's heading, the tenant's when set ('Daily operations'). */
export function groupHeading(moduleId: string, groupId: string, tree: ModuleTree = current): string | undefined {
  return index(tree).byId.get(moduleId)?.groups.find((g) => g.id === groupId)?.heading;
}

/** Is this module switched on? An id the catalog does not know is on. */
export function isModuleOn(id: string, tree: ModuleTree = current): boolean {
  return index(tree).byId.get(id)?.enabled ?? true;
}

/** Is the module behind this tile slug switched on? An unknown tile is on. */
export function isTileOn(tile: string, tree: ModuleTree = current): boolean {
  return index(tree).byTile.get(tile)?.enabled ?? true;
}

/** Is this page ('coportal.hotline-schedule') on? A page the catalog does not know is on. */
export function isPageOn(pageId: string, tree: ModuleTree = current): boolean {
  return index(tree).pages.get(pageId)?.enabled ?? true;
}

/** {@link isPageOn} by rights namespace: the tile slug ('res-tools') and the page slug. */
export function isTilePageOn(tile: string, slug: string, tree: ModuleTree = current): boolean {
  const m = index(tree).byTile.get(tile);
  if (!m) return true;
  return index(tree).pages.get(`${m.id}.${slug}`)?.enabled ?? m.enabled;
}

/**
 * Is this feature ('coportal.logs.key-agent-filter') on? Off when the tenant
 * switched it off or its page or module is off. A feature id the catalog does
 * not know is on (the tests hold every id the code asks about to the catalog).
 */
export function isFeatureOn(id: string, tree: ModuleTree = current): boolean {
  return index(tree).features.get(id) ?? true;
}

/** React form of {@link isFeatureOn}. */
export function useFeature(id: string): boolean {
  return isFeatureOn(id, useModuleTree());
}

/**
 * Is this location a switched-off module or page? The shell renders the
 * not-found page there (not /forbidden: a demo visitor never learns the page
 * exists).
 */
export function isRouteOff(pathname: string, tree: ModuleTree = current): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return index(tree).offRoutes.some(([route, exact]) => path === route || (!exact && path.startsWith(`${route}/`)));
}

/**
 * The module whose rail shows this location, when that is not the module the
 * route belongs to: a template placed inside another module (brief §5.3),
 * e.g. the Bug tracker inside IT answers `/lemax/issues` with IT's rail.
 */
export function hostOfPath(pathname: string, tree: ModuleTree = current): string | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  for (const [route, host] of index(tree).hostRoutes) {
    if (path === route || path.startsWith(`${route}/`)) return host;
  }
  return null;
}

/** The module with this id in the current tree (a team module included). */
export function treeModule(id: string, tree: ModuleTree = current): TreeModule | undefined {
  return index(tree).byId.get(id);
}
